/**
 * wl-collection.js: behaviour for sections/wl-collection.liquid.
 *
 *  - Dropdown filters: one <details> per filter; only one open at a time, Escape and an
 *    outside click close it. On desktop (990px and up) a change re-renders at once; on
 *    phones the same markup is the drawer and Apply submits.
 *  - Re-render: the form is turned into the same filter.* / sort_by / page params the
 *    stock template uses, fetched through the Section Rendering API (section_id) with
 *    the current view and preview_theme_id kept, and the filter bar, grid and pagination
 *    are swapped in. The URL follows with history.replaceState (pushState on a page change).
 *  - Add to cart from a card lives in assets/wl-product-card.js (loaded by the section).
 *  - Analytics: the section only announces CustomEvents (wl-collection:filter, :sort,
 *    :page, wl-pcard:add, wl-pcard:add-failed); assets/analytics-posthog-funnel.js owns
 *    the PostHog event names.
 */
(function () {
  'use strict';

  var DESKTOP = window.matchMedia('(min-width: 990px)');
  var PARAM_RE = /^(filter\.|sort_by$|page$)/;

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function emit(target, name, detail) {
    try {
      (target || document).dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
    } catch (e) { /* old browsers */ }
  }

  ready(function () {
    document.querySelectorAll('[data-wl-col]').forEach(initCollection);
  });

  function initCollection(root) {
    var sectionId = root.getAttribute('data-wl-section-id');
    var form = root.querySelector('[data-wl-col-form]');
    var results = root.querySelector('[data-wl-col-results]');
    if (!form || !results) return;

    var busy = false;
    var priceTimer = null;

    // ----- Read more -------------------------------------------------------------------
    var desc = root.querySelector('[data-wl-desc]');
    var more = root.querySelector('[data-wl-more]');
    if (desc && more) {
      var needsMore = function () { return desc.scrollHeight > desc.clientHeight + 2; };
      var syncMore = function () {
        if (desc.classList.contains('is-open')) return;
        more.hidden = !needsMore();
      };
      syncMore();
      window.addEventListener('resize', syncMore);
      more.addEventListener('click', function () {
        var open = !desc.classList.contains('is-open');
        desc.classList.toggle('is-open', open);
        more.setAttribute('aria-expanded', open ? 'true' : 'false');
        more.querySelector('[data-wl-more-label]').textContent = open ? 'Read Less' : 'Read More';
      });
    }

    // ----- URL building ----------------------------------------------------------------
    function currentBase() {
      var url = new URL(window.location.href);
      var keep = new URLSearchParams();
      url.searchParams.forEach(function (v, k) { if (!PARAM_RE.test(k)) keep.append(k, v); });
      return { path: url.pathname, keep: keep };
    }

    function formUrl() {
      var base = currentBase();
      var params = base.keep;
      var data = new FormData(form);
      data.forEach(function (v, k) {
        if (v === '' || v === null) return;
        params.append(k, v);
      });
      return base.path + '?' + params.toString();
    }

    function withKept(href) {
      // A Shopify-built href (url_to_remove, paginate part) has the filter params but not the
      // view or preview params of this page; put those back.
      var url = new URL(href, window.location.origin);
      var base = currentBase();
      base.keep.forEach(function (v, k) { if (!url.searchParams.has(k)) url.searchParams.set(k, v); });
      return url.pathname + '?' + url.searchParams.toString();
    }

    // ----- Render ----------------------------------------------------------------------
    function render(url, opts) {
      opts = opts || {};
      if (busy) return Promise.resolve();
      busy = true;
      results.setAttribute('aria-busy', 'true');
      var fetchUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);
      var openFilter = (function () {
        var d = root.querySelector('.wl-col__filter[open]');
        return d ? d.getAttribute('data-wl-filter') : null;
      })();
      return fetch(fetchUrl, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
        .then(function (r) { if (!r.ok) throw new Error('section ' + r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newResults = doc.querySelector('[data-wl-col-results]');
          var newFilters = doc.querySelector('[data-wl-col-filters]');
          var filters = root.querySelector('[data-wl-col-filters]');
          if (newResults) {
            results.innerHTML = newResults.innerHTML;
            results.setAttribute('data-wl-count', newResults.getAttribute('data-wl-count') || '');
          }
          if (newFilters && filters) {
            var drawerOpen = filters.hasAttribute('data-open');
            filters.innerHTML = newFilters.innerHTML;
            if (drawerOpen) filters.setAttribute('data-open', '');
            if (openFilter && DESKTOP.matches) {
              var again = filters.querySelector('.wl-col__filter[data-wl-filter="' + openFilter + '"]');
              if (again) again.open = true;
            }
          }
          if (opts.push) history.pushState({ wlCol: true }, '', url);
          else history.replaceState({ wlCol: true }, '', url);
          if (opts.scroll) {
            var top = results.getBoundingClientRect().top + window.pageYOffset - 120;
            window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          }
          var status = root.querySelector('[data-wl-status]');
          var n = Number(results.getAttribute('data-wl-count')) || 0;
          if (status) status.textContent = n + (n === 1 ? ' product' : ' products');
          emit(root, 'wl-collection:updated', { url: url, count: n });
        })
        .catch(function () {
          // The section fetch failed (offline, 5xx); a full navigation still works.
          window.location.assign(url);
        })
        .then(function () {
          busy = false;
          results.setAttribute('aria-busy', 'false');
        });
    }

    function count() { return Number(results.getAttribute('data-wl-count')) || 0; }

    function filterDetail(input) {
      var details = input.closest('.wl-col__filter');
      var name = details ? details.getAttribute('data-wl-filter') : input.name;
      var values = [];
      if (details) {
        details.querySelectorAll('input:checked').forEach(function (i) { values.push(i.value); });
        details.querySelectorAll('input[type="number"]').forEach(function (i) { if (i.value !== '') values.push(i.name.replace(/^filter\.v\.price\./, '') + '=' + i.value); });
      }
      return { filter: name, label: details ? details.getAttribute('data-wl-filter-label') : name, values: values };
    }

    // ----- Filter changes --------------------------------------------------------------
    form.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.name) return;
      if (t.matches('[data-wl-sort]')) {
        var url = formUrl();
        render(url).then(function () { emit(root, 'wl-collection:sort', { sort_by: t.value, result_count: count() }); });
        return;
      }
      if (!PARAM_RE.test(t.name)) return;
      var detail = filterDetail(t);
      if (!DESKTOP.matches) return; // the drawer applies on the button
      var go = function () {
        render(formUrl()).then(function () {
          detail.result_count = count();
          emit(root, 'wl-collection:filter', detail);
        });
      };
      if (t.type === 'number') {
        clearTimeout(priceTimer);
        priceTimer = setTimeout(go, 500);
      } else {
        go();
      }
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      render(formUrl()).then(function () { emit(root, 'wl-collection:filter', { filter: 'all', values: [], result_count: count() }); });
    });

    // Reset one filter, clear all, pagination: all Shopify-built hrefs.
    root.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var reset = t.closest('[data-wl-reset]');
      if (reset) {
        e.preventDefault();
        var details = reset.closest('.wl-col__filter');
        render(withKept(reset.getAttribute('href'))).then(function () {
          emit(root, 'wl-collection:filter', { filter: details ? details.getAttribute('data-wl-filter') : 'reset', values: [], result_count: count() });
        });
        return;
      }
      var clear = t.closest('[data-wl-clear-all]');
      if (clear) {
        e.preventDefault();
        closeDrawer();
        render(withKept(clear.getAttribute('href'))).then(function () {
          emit(root, 'wl-collection:filter', { filter: 'all', values: [], result_count: count() });
        });
        return;
      }
      var page = t.closest('[data-wl-page]');
      if (page) {
        e.preventDefault();
        var label = page.getAttribute('data-wl-page');
        render(withKept(page.getAttribute('href')), { push: true, scroll: true }).then(function () {
          emit(root, 'wl-collection:page', { page: label, result_count: count() });
        });
      }
    });

    window.addEventListener('popstate', function (e) {
      if (e.state && e.state.wlCol) window.location.reload();
    });

    // ----- Dropdown behaviour ----------------------------------------------------------
    root.addEventListener('toggle', function (e) {
      var d = e.target;
      if (!d || !d.matches || !d.matches('.wl-col__filter') || !d.open) return;
      if (!DESKTOP.matches) return; // accordions can stack in the drawer
      root.querySelectorAll('.wl-col__filter[open]').forEach(function (other) { if (other !== d) other.open = false; });
    }, true);

    document.addEventListener('click', function (e) {
      if (!DESKTOP.matches) return;
      var open = root.querySelector('.wl-col__filter[open]');
      if (!open) return;
      if (open.contains(e.target)) return;
      open.open = false;
    });

    // Escape is read on the document: after a re-render the focused input is gone and the key
    // lands on <body>, which is outside the section.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var drawer = root.querySelector('[data-wl-drawer][data-open]');
      if (drawer && !DESKTOP.matches) { closeDrawer(); return; }
      var open = root.querySelector('.wl-col__filter[open]');
      if (open) {
        open.open = false;
        var summary = open.querySelector('summary');
        if (summary) summary.focus();
      }
    });

    // ----- Drawer (phones) -------------------------------------------------------------
    var drawer = root.querySelector('[data-wl-drawer]');
    var openBtn = root.querySelector('[data-wl-drawer-open]');
    var lastFocus = null;

    function focusables() {
      return Array.prototype.filter.call(
        drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, summary, [tabindex]:not([tabindex="-1"])'),
        function (el) { return el.offsetParent !== null; }
      );
    }

    function openDrawer() {
      if (!drawer) return;
      lastFocus = document.activeElement;
      drawer.setAttribute('data-open', '');
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      // Hide everything outside the drawer from assistive tech: the siblings of every ancestor up to body.
      var node = drawer;
      while (node && node.parentElement && node !== document.body) {
        Array.prototype.forEach.call(node.parentElement.children, function (el) {
          if (el === node || el.hasAttribute('aria-hidden') || /^(script|style|link|template)$/i.test(el.tagName)) return;
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('data-wl-drawer-hid', '');
        });
        node = node.parentElement;
      }
      document.documentElement.classList.add('wl-col-drawer-open');
      document.body.style.overflow = 'hidden';
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      var f = focusables();
      if (f.length) f[0].focus();
    }

    function closeDrawer() {
      if (!drawer || !drawer.hasAttribute('data-open')) return;
      drawer.removeAttribute('data-open');
      drawer.removeAttribute('role');
      drawer.removeAttribute('aria-modal');
      document.querySelectorAll('[data-wl-drawer-hid]').forEach(function (el) { el.removeAttribute('aria-hidden'); el.removeAttribute('data-wl-drawer-hid'); });
      document.documentElement.classList.remove('wl-col-drawer-open');
      document.body.style.overflow = '';
      if (openBtn) { openBtn.setAttribute('aria-expanded', 'false'); openBtn.focus(); }
      else if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    if (openBtn) openBtn.addEventListener('click', openDrawer);
    root.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-wl-drawer-close]')) { closeDrawer(); return; }
      if (t.closest('[data-wl-drawer-apply]')) {
        closeDrawer();
        // Same event shape as a desktop change: one per filter group that has values, flat strings.
        var groups = [];
        root.querySelectorAll('.wl-col__filter').forEach(function (d) {
          var i = d.querySelector('input:checked') || d.querySelector('input[type="number"]');
          if (!i) return;
          var detail = filterDetail(i);
          if (detail.values.length) groups.push(detail);
        });
        render(formUrl()).then(function () {
          var n = count();
          if (!groups.length) groups.push({ filter: 'none', label: 'none', values: [] });
          groups.forEach(function (g) { g.result_count = n; g.source = 'drawer'; emit(root, 'wl-collection:filter', g); });
        });
      }
    });

    if (drawer) {
      drawer.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab' || DESKTOP.matches || !drawer.hasAttribute('data-open')) return;
        var f = focusables();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
    }

    DESKTOP.addEventListener ? DESKTOP.addEventListener('change', function (mq) { if (mq.matches) closeDrawer(); })
      : DESKTOP.addListener(function (mq) { if (mq.matches) closeDrawer(); });

    // Add to cart from a card: assets/wl-product-card.js (document-level, every page with cards).
  }
})();
